import React, { useState, useEffect } from 'react'
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import { Button, FormControl, RadioGroup, FormControlLabel, Radio, Checkbox } from '@material-ui/core';
import HttpService from '../services/httpService';
import { connect } from "react-redux";
import { setLoading, setSnackInfo } from '../redux/actions/appAction';

const emptyRow = () => ({ materialId: null, unitId: null, quantity: '', vendor: null, materialVendors: [], rate: '' })

const todayDateString = () => new Date().toISOString().split('T')[0]

const RaiseAdhocPoSoModal = (props) => {

	// Services-scope projects only ever raise Ad-hoc SO (no material PO), so default straight to SO.
	const [type, setType] = useState(props.SCOPE === 'Services' ? 'SO' : 'PO')
	const [materials, setMaterials] = useState([])
	const [units, setUnits] = useState([])
	const [rows, setRows] = useState([emptyRow()])

	const [expectedDeliveryDate, setExpectedDeliveryDate] = useState('')
	const [reason, setReason] = useState('')

	const [gstRequired, setGstRequired] = useState(false)
	const [gstDetails, setGstDetails] = useState({ gst: "", rate: "" })
	const [discountPercent, setDiscountPercent] = useState('') // entered as a % of Item Value, not a flat ₹ amount
	const [freight, setFreight] = useState('')
	const [submitting, setSubmitting] = useState(false)

	const gstPercent = ["5", "12", "18", "28"]

	const [preview, setPreview] = useState({
		preCumulativeValue: 0,
		currentTransactionValue: 0,
		postCumulativeValue: 0,
		approvalFlow: [],
		blocked: false
	})

	useEffect(() => {
		getMaterials()
		getUnits()
		// eslint-disable-next-line
	}, [])

	const getMaterials = async () => {
		try {
			const result = await HttpService.getAllMaterialsNameAndCode()
			setMaterials(result.data)
		} catch (error) {
			console.error(error)
			props.setSnackInfo("Couldn't fetch item master list", "error")
		}
	}

	const getUnits = async () => {
		try {
			const result = await HttpService.getAllUnits()
			setUnits(result.data)
		} catch (error) {
			console.error(error)
		}
	}

	const updateRow = (index, changes) => {
		setRows(rows.map((row, i) => i === index ? { ...row, ...changes } : row))
	}

	// Vendor + pricePerUnit options are specific to the selected Item Master, so the Rate field can auto-fill.
	const handleMaterialSelect = async (index, v) => {
		updateRow(index, { materialId: v, vendor: null, rate: '', materialVendors: [] })
		if (!v) return
		try {
			const result = await HttpService.getVendorsForMaterial(v._id)
			updateRow(index, { materialId: v, vendor: null, rate: '', materialVendors: result.data })
		} catch (error) {
			console.error(error)
			props.setSnackInfo("Couldn't fetch vendors for this item", "error")
		}
	}

	const handleVendorSelect = (index, v) => {
		if (!v) {
			updateRow(index, { vendor: null, rate: '' })
			return
		}
		let missingDocs = ''
		if (!v.vendorId?.aadharCardLink || v.vendorId.aadharCardLink.length < 1) {
			missingDocs += 'Aadhar Card,'
		}
		if (!v.vendorId?.panCardLink || v.vendorId.panCardLink.length < 1) {
			missingDocs += 'PAN Card,'
		}
		if (missingDocs.length > 0) {
			props.setSnackInfo(`${v.vendorId?.name} did not provide ${missingDocs} can't raise PO/SO for them.`, 'error')
			return
		}
		updateRow(index, { vendor: v, rate: v.pricePerUnit })
	}

	const getPreview = async (currentValue) => {
		try {
			// Ad-hoc PO and Ad-hoc SO share one combined ₹50,000 cumulative cap per project.
			const result = await HttpService.getAdhocPreview(props.SELECTEDPROJECT._id, type, currentValue)
			setPreview(result.data)
		} catch (error) {
			console.error(error)
		}
	}

	useEffect(() => {
		const itemValue = rows.reduce((sum, row) => sum + (Number(row.quantity) || 0) * (Number(row.rate) || 0), 0)
		const discountAmount = type === 'PO' ? (itemValue * (Number(discountPercent) || 0)) / 100 : 0
		const freightAmount = type === 'PO' ? (Number(freight) || 0) : 0
		const taxableValue = itemValue - discountAmount
		const cumulativeValue = taxableValue + freightAmount

		getPreview(cumulativeValue)
		// eslint-disable-next-line
	}, [type, rows, discountPercent, freight, gstRequired, gstDetails])

	const clear = () => {
		setType(props.SCOPE === 'Services' ? 'SO' : 'PO')
		setRows([emptyRow()])
		setExpectedDeliveryDate('')
		setReason('')
		setGstRequired(false)
		setGstDetails({ gst: "", rate: "" })
		setDiscountPercent('')
		setFreight('')
	}

	const handleSubmit = async () => {
		const hasIncompleteRow = rows.some(row => !row.materialId || !row.unitId || !row.vendor || !row.quantity || !row.rate)
		if (rows.length === 0 || hasIncompleteRow) {
			return props.setSnackInfo('Please fill all the fields for every material row', 'error')
		}
		if (preview.blocked) {
			return props.setSnackInfo('Ad-hoc PO/SO cannot be created because the cumulative Ad-hoc value for this project exceeds ₹50,000.', 'error')
		}
		if (expectedDeliveryDate && expectedDeliveryDate < todayDateString()) {
			return props.setSnackInfo('Expected Delivery Date cannot be a past date', 'error')
		}
		if (submitting) return
		setSubmitting(true)
		props.setLoading(true)
		try {
			const data = {
				projectId: props.SELECTEDPROJECT._id,
				type,
				scope: props.SCOPE,
				materials: rows.map(row => ({
					materialId: row.materialId._id,
					unitId: row.unitId._id,
					quantity: Number(row.quantity),
					vendorId: row.vendor.vendorId._id,
					rate: Number(row.rate)
				})),
				expectedDeliveryDate,
				reason,
				gstRequired,
				gstDetails: gstRequired ? gstDetails : undefined,
				// Sent as a raw % (of Item Value) — AdhocTransaction.discount / Procurement.discount
				// store the percent itself, not a computed ₹ amount. The backend converts to ₹
				// wherever it needs an amount (cap check, PO PDF via calcPoPrice's AD-HOC branch).
				discount: Number(discountPercent) || 0,
				freight: Number(freight) || 0
			}
			await HttpService.createAdhocPoSo(data)
			props.setSnackInfo(`Ad-hoc ${type} raised for approval`, 'success')
			clear()
			props.CLOSEMODAL()
		} catch (error) {
			console.error(error)
			props.setSnackInfo(error?.response?.data?.message ?? 'Something Went Wrong', 'error')
		}
		props.setLoading(false)
		setSubmitting(false)
	}

	return (
		<div>
			<div className='modal-header' style={{ justifyContent: "space-between", paddingBottom: 10 }}>
				<div style={{ width: "48%" }}>
					<b>Project: </b>
					<span>{`${props.PROJECTINFO?.projectCode}_${props.PROJECTINFO?.clientName}`}</span>
				</div>
				<div style={{ width: "48%" }}>
					<b>Scope: </b>
					<span>{props.SCOPE}</span>
				</div>
			</div>

			<FormControl component="fieldset" style={{ marginBottom: 10 }}>
				<RadioGroup row aria-label="adhocType" name="adhocType" value={type} onChange={(e) => setType(e.target.value)}>
					{props.SCOPE !== 'Services' && <FormControlLabel value="PO" control={<Radio />} label="Ad-hoc PO" />}
					{props.SCOPE === 'Services' && <FormControlLabel value="SO" control={<Radio />} label="Ad-hoc SO" />}
				</RadioGroup>
			</FormControl>

			<TableContainer component={Paper} style={{ marginBottom: 10 }}>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell>Sl No</TableCell>
							<TableCell>Item Master</TableCell>
							<TableCell>UOM</TableCell>
							<TableCell>Quantity</TableCell>
							<TableCell>Vendor</TableCell>
							<TableCell>Rate</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{rows.map((row, i) => (
							<TableRow key={i}>
								<TableCell>{i + 1}</TableCell>
								<TableCell>
									<Autocomplete
										value={row.materialId}
										onChange={(e, v) => handleMaterialSelect(i, v)}
										options={materials}
										getOptionLabel={(option) => option?.name || ''}
										style={{ width: 220 }}
										renderInput={(params) => <TextField {...params} variant="outlined" />}
									/>
								</TableCell>
								<TableCell>
									<Autocomplete
										value={row.unitId}
										onChange={(e, v) => updateRow(i, { unitId: v })}
										options={units}
										getOptionLabel={(option) => option?.name || ''}
										style={{ width: 120 }}
										renderInput={(params) => <TextField {...params} variant="outlined" />}
									/>
								</TableCell>
								<TableCell>
									<TextField
										type="number"
										value={row.quantity}
										onChange={(e) => updateRow(i, { quantity: e.target.value })}
										variant="outlined"
										inputProps={{ min: 1 }}
										style={{ width: 100 }}
									/>
								</TableCell>
								<TableCell>
									<Autocomplete
										value={row.vendor}
										onChange={(e, v) => handleVendorSelect(i, v)}
										options={row.materialVendors}
										getOptionLabel={(option) => option?.vendorId?.name || ''}
										disabled={!row.materialId}
										style={{ width: 200 }}
										renderInput={(params) => <TextField {...params} variant="outlined" placeholder={!row.materialId ? "Select item first" : ""} />}
									/>
								</TableCell>
								<TableCell>
									<TextField
										type="number"
										value={row.rate}
										onChange={(e) => updateRow(i, { rate: e.target.value })}
										variant="outlined"
										style={{ width: 100 }}
									/>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>

			<div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 10 }}>
				<TextField
					id="adhoc-delivery-date"
					label="Expected Delivery Date"
					type="date"
					value={expectedDeliveryDate}
					onChange={(e) => setExpectedDeliveryDate(e.target.value)}
					InputLabelProps={{ shrink: true }}
					inputProps={{ min: todayDateString() }}
					variant="outlined"
					style={{ width: 200 }}
				/>
				<TextField
					id="adhoc-reason"
					label="Reason / Remarks"
					value={reason}
					onChange={(e) => setReason(e.target.value)}
					variant="outlined"
					style={{ width: 400 }}
				/>
			</div>

			<div style={{ marginBottom: 15 }}>
				<div style={{ marginBottom: 10 }}>Add GST{type === 'PO' && ' and extra charge'} details</div>
				<div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
					{type === 'PO' && <>
						<TextField
							id="adhoc-discount"
							label="Discount (%)"
							type="number"
							value={discountPercent}
							onChange={(e) => setDiscountPercent(e.target.value)}
							variant="outlined"
							inputProps={{ min: 0, max: 100 }}
							style={{ width: 150 }}
						/>
						<TextField
							id="adhoc-freight"
							label="Freight"
							type="number"
							value={freight}
							onChange={(e) => setFreight(e.target.value)}
							variant="outlined"
							style={{ width: 150 }}
						/>
					</>}
					<span style={{ border: '1.5px solid #ccc', padding: '5.5px 0px', borderRadius: '3.5px' }}>
						<Checkbox
							checked={gstRequired}
							onChange={(e) => setGstRequired(e.target.checked)}
							inputProps={{ 'aria-label': 'gst required checkbox' }}
						/>GST Required
					</span>
				</div>
				{gstRequired && <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
					<FormControl component="fieldset" style={{ border: '1.5px solid #ccc', padding: '5.5px 10px', borderRadius: '3.5px' }}>
						<RadioGroup row aria-label="gstType" name="gstType" value={gstDetails.gst} onChange={(e) => setGstDetails({ ...gstDetails, gst: e.target.value })}>
							<FormControlLabel value="SGST" control={<Radio />} label="CGST/SGST" />
							<FormControlLabel value="IGST" control={<Radio />} label="IGST" />
						</RadioGroup>
					</FormControl>
					<Autocomplete
						value={gstDetails.rate}
						onChange={(e, value) => setGstDetails({ ...gstDetails, rate: value })}
						id="adhoc-gst-percent"
						options={gstPercent}
						getOptionLabel={(option) => `${option}%`}
						style={{ width: 200 }}
						renderInput={(params) => <TextField {...params} label={`Select ${gstDetails.gst || 'GST'} percent`} variant="outlined" />}
					/>
				</div>}
			</div>

			<div style={{ border: '1.5px solid #ccc', borderRadius: 5, padding: 15, marginBottom: 15 }}>
				<div><b>Current Approved Ad-hoc Value: </b>₹{Number(preview.preCumulativeValue || 0).toFixed(2)}</div>
				<div><b>Current Transaction Value: </b>₹{Number(preview.currentTransactionValue || 0).toFixed(2)}</div>
				<div><b>Projected Cumulative Value: </b>₹{Number(preview.postCumulativeValue || 0).toFixed(2)}</div>
				<div style={{ marginTop: 8 }}>
					<b>Applicable Approval Flow: </b>
					{preview.approvalFlow.length > 0
						? preview.approvalFlow.join(' → ')
						: 'N/A'}
				</div>
				{preview.blocked &&
					<div style={{ color: 'red', marginTop: 8 }}>
						Ad-hoc PO/SO cannot be created because the cumulative Ad-hoc value for this project exceeds ₹50,000.
					</div>
				}
			</div>

			<Button
				variant="contained"
				color="primary"
				style={{ background: "#355025", color: "#FFF2EB" }}
				onClick={handleSubmit}
				disabled={preview.blocked || submitting}
			>
				{submitting ? 'Submitting...' : 'Submit'}
			</Button>
		</div>
	)
}

export default connect(null, { setLoading, setSnackInfo })(RaiseAdhocPoSoModal)
